
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Camera, Upload, Trash2 } from 'lucide-react';
import { SecureProfilePhoto } from './SecureProfilePhoto';
import { photoUploadService } from '@/services/photoUploadService';
import { toast } from 'sonner';

interface SecurePhotoUploadProps {
  currentPhotoPath?: string | null;
  userId: string;
  fallbackInitials?: string;
  onPhotoUpdated: (newPhotoPath: string | null) => void;
  disabled?: boolean;
}

export const SecurePhotoUpload: React.FC<SecurePhotoUploadProps> = ({
  currentPhotoPath,
  userId,
  fallbackInitials,
  onPhotoUpdated,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || disabled) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const result = await photoUploadService.replaceProfilePhoto(
        currentPhotoPath || '', 
        file, 
        userId
      );

      if (result.success && result.url) {
        onPhotoUpdated(result.url);
        toast.success('Profile photo updated successfully!');
      } else {
        toast.error(result.error || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      // Clear the input so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async () => {
    if (!currentPhotoPath || disabled) return;

    setDeleting(true);
    
    try {
      const success = await photoUploadService.deleteProfilePhoto(currentPhotoPath);
      
      if (success) {
        onPhotoUpdated(null);
        toast.success('Profile photo deleted successfully!');
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      console.error('Photo delete error:', error);
      toast.error('Failed to delete photo');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="bg-gray-700 border-gray-600">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Camera className="h-5 w-5" />
          Profile Photo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <SecureProfilePhoto 
            filePath={currentPhotoPath}
            fallbackInitials={fallbackInitials}
            size="lg"
          />
          
          <div className="flex-1">
            <div className="flex flex-wrap gap-3 mb-3">
              <Label htmlFor="profile_photo_upload" className="cursor-pointer">
                <div className={`bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2 ${
                  disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}>
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      {currentPhotoPath ? 'Replace Photo' : 'Upload Photo'}
                    </>
                  )}
                </div>
              </Label>
              
              {currentPhotoPath && (
                <Button
                  onClick={handleDeletePhoto}
                  disabled={disabled || deleting}
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Photo
                    </>
                  )}
                </Button>
              )}
            </div>
            
            <Input
              id="profile_photo_upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={disabled || uploading}
              className="hidden"
            />
            
            <div className="text-sm text-gray-400 space-y-1">
              <p>Upload a clear photo of yourself</p>
              <p>Supported formats: JPG, PNG, GIF (max 5MB)</p>
              <p className="text-xs text-yellow-400">
                🔒 Your photo is stored privately and only you can view it
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
